import styled from '@emotion/styled'
import { ScreenUtil } from '@mathflat/videoplayer'
import { observer } from 'mobx-react'
import qs from 'qs'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import SimpleBar from 'simplebar-react'

import { useLearningTime } from '~/@common/hooks/useLearningTime'
import { useRepository } from '~/@common/hooks/useRepository'
import { webviewService } from '~/@common/services'
import { nativeEventBus } from '~/@common/services/nativeEventBus'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import LectureVideoPlayer from '~/@pages/@common/(LectureMaterial)/LectureVideoPlayer'
import GlobalHeader from '~/@pages/@widgets/(Navigation)/GlobalHeader/GlobalHeader'

import type { LectureGroup as LectureGroupT } from '../@service/Lecture.service'
import { LectureService } from '../@service/Lecture.service'
import { LECTURE_GROUP_CARD_SIZE } from '../@widgets/LectureGroup.style'
import LectureGroup from '../@widgets/LectureGroup.widget'

const LectureDetailPage = () => {
  useLearningTime()
  const params = useParams<{ lectureId: string; assignmentId?: string }>()
  const [searchParams] = useSearchParams()

  const [lectureGroup, setLectureGroup] = useState<LectureGroupT>()

  const service = useRepository(LectureService)

  const searchParam = qs.parse(searchParams.toString())
  const isListHidden = searchParam && searchParam.hideList === 'true'
  const parsedAssignmentId = params.assignmentId ? parseInt(params.assignmentId, 10) : NaN
  const assignmentId = Number.isNaN(parsedAssignmentId) ? undefined : parsedAssignmentId

  const lectureIdParam = params.lectureId ? parseInt(params.lectureId, 10) : NaN
  const lectureId = Number.isNaN(lectureIdParam) ? undefined : lectureIdParam
  const lecture = lectureId
    ? lectureGroup?.middleChapterLectures.find((lecture) => lecture.id === lectureId)
    : undefined

  const handleProgressSubmit = async () => {
    if (lecture && commonRepo.studentId) {
      await service.setProgress({
        lecture,
        studentId: commonRepo.studentId,
        assignmentId,
      })
    }
  }
  const handleAssignmentComplete = async () => {
    if (assignmentId == null) {
      return
    }
    await service.setAssignmentStatusCompleted(assignmentId)
  }

  useEffect(() => {
    if (params.lectureId && commonRepo.studentId) {
      service
        .getLectureGroupById(parseInt(params.lectureId), commonRepo.studentId)
        .then((lectureGroup) => {
          setLectureGroup(lectureGroup)
        })
        .catch((e) => {
          console.error(e)
        })
    }
  }, [params.lectureId, service])

  // 앱의 화면 가로 전환에 대한 락을 풀고, 가로 전환되었을때 이벤트를 감지하여 풀스크린 이벤트 실행.
  useEffect(() => {
    webviewService.unlockDeviceOrientation()
    const unsub = nativeEventBus.subscribe('onOrientationLandscape', ScreenUtil.handleFullScreen)
    return () => {
      webviewService.lockDeviceOrientation()
      unsub()
    }
  }, [])

  if (!lectureGroup || !commonRepo.studentAppSetting) return <></>
  const lectureGroupForDisplay =
    commonRepo.studentAppSetting.lectureService || !lecture
      ? lectureGroup
      : { ...lectureGroup, middleChapterLectures: [lecture] }

  return (
    <>
      <PortalRootLayoutHeader>
        <GlobalHeader SlotCenter={lecture?.title} />
      </PortalRootLayoutHeader>
      <SimpleBar style={{ maxHeight: '100%' }}>
        <S.LectureDetailPage>
          <div className="lecture-video-area">
            {lecture && (
              <>
                <LectureVideoPlayer
                  selectedLecture={lecture}
                  onProgressSubmit={handleProgressSubmit}
                  onAssignmentComplete={assignmentId != null ? handleAssignmentComplete : undefined}
                />
                <div className="lecture-desc">
                  <div className="title">{lecture.title}</div>
                  <div className="sub-title">
                    {
                      commonRepo.curriculumCore?.getItemByTrieKey(
                        service.makeCurriculumKeyForGradeSemester(lecture.trieKey),
                      )?.label
                    }
                    &nbsp;|&nbsp;{lecture.bigChapterName}&nbsp;|&nbsp;{lecture.middleChapterName}
                  </div>
                </div>
              </>
            )}
          </div>
          {!isListHidden && (
            <div className="lecture-group">
              <LectureGroup lectureGroup={lectureGroupForDisplay} />
            </div>
          )}
        </S.LectureDetailPage>
      </SimpleBar>
    </>
  )
}

export default observer(LectureDetailPage)

const S = {
  LectureDetailPage: styled.div`
    width: 100%;
    height: 100%;

    display: flex;
    gap: 24px;

    ${mediaQuery.underTablet} {
      flex-direction: column;
      gap: 20px;
      overflow: auto;
    }

    .lecture-video-area {
      flex: 1 0 0;
      ${mediaQuery.underTablet} {
        flex: 0 1 auto;
      }

      .lecture-desc {
        display: flex;
        flex-direction: column;
        gap: 8px;

        margin: 20px 16px 0px 16px;

        .title {
          color: var(--Colors-Gray-900);
          ${typo.heading05};
          font-weight: bold;
        }
        .sub-title {
          ${typo.body02};
          color: var(--Colors-Gray-700);
          font-weight: bold;
        }
      }
    }

    .lecture-group {
      width: ${LECTURE_GROUP_CARD_SIZE}px;

      ${mediaQuery.underTablet} {
        width: auto;
        overflow: scroll;

        margin: 0px 16px;
      }
    }
  `,
}
